import { StoreCard } from '@/components/store/store-card';
import { externalToolCard } from '@/lib/store/card';
import { goHref, trendPlacement, type TrendSlotPosition } from '@/lib/store/placement';
import type { SlotEntry } from '@/lib/store/recommendation-slot';
import type { ToolSummary } from '@/modules/directory/types';
import { m } from '@/paraglide/messages.js';
import { storePriceLabel } from './card-labels';

// Slot cards never show the "new" badge, so the reference time is irrelevant;
// a fixed epoch keeps SSR and hydration output identical.
const NO_NEW_BADGE = new Date(0);

function SlotCards({
  trendSlug,
  entries,
  position,
  compact = false,
}: {
  trendSlug: string;
  entries: SlotEntry<ToolSummary>[];
  position: TrendSlotPosition;
  compact?: boolean;
}) {
  return (
    <div className={compact ? 'grid gap-3 sm:grid-cols-3' : 'grid gap-4 md:grid-cols-3'}>
      {entries.map((entry) => {
        const card = externalToolCard(entry.tool, { now: NO_NEW_BADGE });
        return (
          <StoreCard
            key={card.id}
            card={card}
            priceLabel={storePriceLabel(card.price)}
            marker={entry.source === 'sponsored' ? m['directory.store.slot.sponsored']() : undefined}
            detailsLabel={compact ? undefined : m['directory.store.slot.details']()}
            action={{
              href: goHref(entry.tool.slug, trendPlacement(trendSlug, position)),
              label: m['directory.store.slot.visit'](),
              external: true,
              rel: entry.rel,
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * SSR slot section on the trend page, between the credit packs and the FAQ
 * (plan §4.5). Renders nothing when the slot is empty.
 */
export function RecommendationSlotSection({
  trendSlug,
  entries,
}: {
  trendSlug: string;
  entries: SlotEntry<ToolSummary>[];
}) {
  if (entries.length === 0) return null;
  return <SlotCards trendSlug={trendSlug} entries={entries} position="section" />;
}

/** Compact slot under finished renders (client-side, after success or failure). */
export function RecommendationStrip({
  trendSlug,
  entries,
}: {
  trendSlug: string;
  entries: SlotEntry<ToolSummary>[];
}) {
  if (entries.length === 0) return null;
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-foreground">{m['directory.store.slot.strip_title']()}</h3>
      <SlotCards trendSlug={trendSlug} entries={entries} position="result" compact />
    </div>
  );
}
